import type { CalendarPlatform } from "@/types/contentCalendar.type";
import type { CoverLockReason } from "@/types/scheduling.type";

// Which covers each platform takes. Mirrors the backend's
// domain/Scheduling/coverRules.ts, which refuses the rest; these let the UI say
// so before anything goes up.

/**
 * Platforms that take no cover picture: TikTok's API only takes a moment of
 * the video. Their cover stays hidden until there is a way to send one.
 */
export const COVERLESS_PLATFORMS: readonly string[] = ["TIKTOK"];

export function platformTakesCover(platform: string): boolean {
  return !COVERLESS_PLATFORMS.includes(platform);
}

// YouTube's custom-thumbnail limits. Any cover may be uploaded, but a video
// that posts to YouTube can only carry one YouTube takes.
export const YOUTUBE_COVER_MAX_BYTES = 2 * 1024 * 1024;
const YOUTUBE_COVER_TYPES = ["image/jpeg", "image/png"];

/** True when a file for `forPlatform` (null: every platform) can post to YouTube. */
export function fileReachesYouTube(
  forPlatform: CalendarPlatform | string | null | undefined,
  channels: readonly string[],
): boolean {
  if (forPlatform) return forPlatform === "YOUTUBE";
  return channels.includes("YOUTUBE");
}

/** Why YouTube would refuse this cover, or null when it fits. */
export function youtubeCoverProblem(cover: { type: string; size: number }): string | null {
  // A browser-drawn frame is always a JPEG; an empty type is one of those.
  const type = cover.type || "image/jpeg";
  if (!YOUTUBE_COVER_TYPES.includes(type)) return "YouTube only takes a JPEG or PNG cover.";
  if (cover.size > YOUTUBE_COVER_MAX_BYTES) return "YouTube caps covers at 2 MB.";
  return null;
}

/** Where a YouTube channel's owner turns on custom thumbnails. */
export const YOUTUBE_VERIFY_URL = "https://www.youtube.com/verify";

/**
 * Why the platform won't use the cover, said before the post is scheduled.
 * A NOT_VERIFIED channel's line goes with a link to YOUTUBE_VERIFY_URL.
 */
export function coverLockMessage(reason: CoverLockReason): string {
  return reason === "CHANNEL_NOT_VERIFIED"
    ? "YouTube will use a frame of its own, not this cover, until the channel is phone-verified."
    : "YouTube will use a frame of its own, not this cover: it has limited this channel's features. YouTube Studio says why.";
}
